import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  listReparatieRoutesForReparateur,
  planReparatieHeenRit,
  planReparatieTerugRit,
  type RouteOption,
} from '../../lib/reparatie';

type Mode = 'heen' | 'terug';

interface Props {
  reparateurId: string;
  reparateurNaam?: string | null;
  mode: Mode;
  /** wiId voor heen, of lijst wiIds voor terug-bulk */
  wiId?: string;
  wiIds?: string[];
  /** WI-naam voor heen-mode (cosmetisch) */
  wiLabel?: string | null;
  /** WI-namen voor terug-mode (cosmetisch) */
  wiLabels?: string[];
  /** rit-Id van bestaande koppeling (override-context) — niet vereist */
  currentRitId?: string | null;
  onClose: () => void;
  /**
   * `vers` draagt de zojuist gekoppelde rit. De aanroeper gebruikt dat om de
   * mirror-lag te overbruggen: de rit staat al in Salesforce, maar de mirror
   * waar de kaart uit leest is pas bij de volgende sync bij.
   */
  onPlanned: (vers?: VersGeplandeRit) => void;
}

export interface VersGeplandeRit {
  mode: Mode;
  wiIds: string[];
  ritId: string | null;
  datum: string | null;
}

export function PlanRitDialog({
  reparateurId,
  reparateurNaam,
  mode,
  wiId,
  wiIds,
  wiLabel,
  wiLabels,
  currentRitId,
  onClose,
  onPlanned,
}: Props) {
  const [options, setOptions] = useState<RouteOption[]>([]);
  const [selectedRouteId, setSelectedRouteId] = useState<string>('');
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingOptions(true);
      setError(null);
      try {
        const list = await listReparatieRoutesForReparateur(reparateurId);
        if (cancelled) return;
        setOptions(list);
        const initial = list.find((o) => o.routeId === currentRitId)?.routeId
          || list[0]?.routeId
          || '';
        setSelectedRouteId(initial);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Routes laden mislukt');
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reparateurId, currentRitId]);

  const targetCount = mode === 'heen' ? 1 : (wiIds?.length ?? 0);
  const canPlan = useMemo(() => {
    if (busy || loadingOptions || !selectedRouteId) return false;
    if (mode === 'heen') return Boolean(wiId);
    return Array.isArray(wiIds) && wiIds.length > 0;
  }, [busy, loadingOptions, selectedRouteId, mode, wiId, wiIds]);

  // Terugval als Apex geen datum meegeeft: de datum van de optie die hier is
  // aangeklikt. Die is per definitie juist, want het is de rit die we boeken.
  const gekozenDatum = options.find((o) => o.routeId === selectedRouteId)?.datum ?? null;

  const handlePlan = async () => {
    if (!selectedRouteId) return;
    setBusy(true);
    setError(null);
    try {
      let vers: VersGeplandeRit;
      if (mode === 'heen') {
        if (!wiId) throw new Error('wiId ontbreekt');
        const res = await planReparatieHeenRit({ wiId, geplandeRitId: selectedRouteId });
        if (!res.success) {
          setError(res.message || 'Plannen mislukt');
          setBusy(false);
          return;
        }
        vers = {
          mode,
          wiIds: [wiId],
          ritId: res.geplandeRitId ?? null,
          datum: res.geplandeRitDatum ?? gekozenDatum,
        };
      } else {
        if (!wiIds || wiIds.length === 0) throw new Error('wiIds ontbreekt');
        const res = await planReparatieTerugRit({
          geplandeRitId: selectedRouteId,
          wiIds,
        });
        if (!res.success) {
          setError(res.message || 'Plannen mislukt');
          setBusy(false);
          return;
        }
        vers = {
          mode,
          wiIds,
          ritId: res.geplandeRitId ?? null,
          datum: res.geplandeRitDatum ?? gekozenDatum,
        };
      }
      onPlanned(vers);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Plannen mislukt');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">
            {mode === 'heen' ? 'Plan heen-rit naar reparateur' : 'Plan terug-rit van reparateur'}
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            {reparateurNaam ?? 'Reparateur'}
            {mode === 'heen' && wiLabel ? ` — ${wiLabel}` : ''}
            {mode === 'terug' && targetCount > 0
              ? ` — ${targetCount} ${targetCount === 1 ? 'kleed' : 'kleden'}`
              : ''}
          </p>
        </div>

        <div className="px-6 py-4 space-y-3">
          {mode === 'terug' && wiLabels && wiLabels.length > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded p-2 max-h-24 overflow-auto">
              <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1">
                Geselecteerde kleden
              </p>
              <ul className="text-xs text-slate-700 space-y-0.5 font-mono">
                {wiLabels.map((label, idx) => (
                  <li key={`${label}-${idx}`}>{label}</li>
                ))}
              </ul>
            </div>
          )}

          {loadingOptions && <p className="text-sm text-slate-500">Routes laden…</p>}

          {!loadingOptions && options.length === 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3">
              <p className="text-sm text-amber-900">Geen route-opties gevonden.</p>
              <p className="text-xs text-amber-700 mt-1">
                Mogelijk valt het reparateur-adres buiten elk dienstgebied. Plan handmatig
                een rit via Salesforce of pas de Account-postcode aan.
              </p>
            </div>
          )}

          {!loadingOptions && options.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Kies rit
              </label>
              <select
                className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 bg-white"
                value={selectedRouteId}
                onChange={(e) => setSelectedRouteId(e.target.value)}
                disabled={busy}
              >
                {options.map((o) => (
                  <option key={o.routeId} value={o.routeId}>
                    {o.label || formatOptionLabel(o)}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-slate-400 mt-1">
                Bestaande reparateur-stops worden hergebruikt; nieuwe routes worden
                pas aangemaakt als er geen bestaande stop binnen 7 dagen is.
              </p>
            </div>
          )}

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handlePlan}
            disabled={!canPlan}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              canPlan
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : 'bg-slate-200 text-slate-500 cursor-not-allowed'
            )}
          >
            {busy ? 'Bezig…' : mode === 'heen' ? 'Koppel aan rit' : `Koppel ${targetCount} kleden`}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatOptionLabel(o: RouteOption): string {
  const parts: string[] = [];
  if (o.datum) parts.push(o.datum);
  else if (o.datumISO) parts.push(o.datumISO);
  if (o.routeNaam) parts.push(o.routeNaam);
  if (o.tijdVanaf && o.tijdTot) parts.push(`${o.tijdVanaf}-${o.tijdTot}`);
  return parts.join(' — ') || o.routeId;
}
